'use client'

import styled from '@emotion/styled'
import AccountBalanceIcon from '@mui/icons-material/AccountBalance'
import BusinessIcon from '@mui/icons-material/Business'
import GroupIcon from '@mui/icons-material/Group'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import MuiButton from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Container from '@mui/material/Container'
import FormControl from '@mui/material/FormControl'
import Grid from '@mui/material/Grid'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { motion } from 'framer-motion'
import { useState } from 'react'
import client from '~/lib/client/client'

const Badge = styled(Box)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(39, 177, 198, 0.1);
  border: 1px solid rgba(39, 177, 198, 0.2);
  border-radius: 9999px;
  padding: 6px 20px;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #27b1c6;
  margin-bottom: 24px;
`

const StyledCard = styled(Card)`
  height: 100%;
  background: #fafafa;
  background-image: none;
  border: none;
  box-shadow:
    0 1px 2px -1px rgba(0, 0, 0, 0.1),
    0 4px 6px -1px rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  text-align: center;
  transition: all 0.3s;

  &:hover {
    box-shadow: 0 8px 32px rgba(39, 177, 198, 0.15);
    transform: translateY(-4px);
  }
`

const IconBox = styled(Box)`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: linear-gradient(
    135deg,
    rgba(39, 177, 198, 0.2),
    rgba(39, 177, 198, 0.1)
  );
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
`

const clientTypes = [
  {
    icon: <BusinessIcon sx={{ fontSize: 32, color: '#27b1c6' }} />,
    title: 'Corporate Clients',
    desc: 'Businesses seeking strategic investment management',
  },
  {
    icon: <AccountBalanceIcon sx={{ fontSize: 32, color: '#27b1c6' }} />,
    title: 'Institutional Investors',
    desc: 'Financial institutions requiring advanced strategies',
  },
  {
    icon: <GroupIcon sx={{ fontSize: 32, color: '#27b1c6' }} />,
    title: 'High Net Worth Individuals',
    desc: 'Private clients with significant investment portfolios',
  },
]

export default function ConsultationPage() {
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [form, setForm] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    clientType: '',
    investmentRange: '',
    message: '',
  })

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    const msg = [
      form.company && `Company: ${form.company}`,
      form.phone && `Phone: ${form.phone}`,
      form.clientType && `Client Type: ${form.clientType}`,
      form.investmentRange && `Investment Range: ${form.investmentRange}`,
      form.message,
    ]
      .filter(Boolean)
      .join('\n')

    const [, err] = await client({
      path: '/api/contact/messages',
      method: 'POST',
      body: { name: form.name, email: form.email, message: msg },
    })

    setLoading(false)

    if (err) {
      setError(err.say || 'Failed to send. Please try again.')
      return
    }

    setSuccess(true)
    setForm({
      name: '',
      company: '',
      email: '',
      phone: '',
      clientType: '',
      investmentRange: '',
      message: '',
    })
  }

  return (
    <Box
      sx={{
        py: 12,
        pt: 'calc(var(--header-height) + 48px)',
        background: 'linear-gradient(180deg, #ffffff 0%, #f5f5f5 50%, #ffffff 100%)',
      }}
    >
      <Container maxWidth='lg'>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Badge>Institutional Services</Badge>
          <Typography variant='h2' sx={{ fontWeight: 700, mb: 3, color: '#171717' }}>
            Institutional & High Net Worth Consultation
          </Typography>
          <Typography
            sx={{
              color: '#6b7280',
              fontSize: '1.125rem',
              maxWidth: 700,
              mx: 'auto',
            }}
          >
            For corporate clients, institutional investors, and high net worth
            individuals requiring tailored investment strategies and dedicated
            support
          </Typography>
        </Box>

        {/* Client Types */}
        <Grid container spacing={3} sx={{ mb: 8, maxWidth: 900, mx: 'auto' }}>
          {clientTypes.map((ct, i) => (
            <Grid key={i} size={{ xs: 12, sm: 4 }}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                style={{ height: '100%' }}
              >
                <StyledCard elevation={0}>
                  <CardContent sx={{ p: 4 }}>
                    <IconBox>{ct.icon}</IconBox>
                    <Typography
                      variant='subtitle1'
                      sx={{ fontWeight: 700, mb: 1, color: '#171717' }}
                    >
                      {ct.title}
                    </Typography>
                    <Typography variant='body2' sx={{ color: '#9ca3af' }}>
                      {ct.desc}
                    </Typography>
                  </CardContent>
                </StyledCard>
              </motion.div>
            </Grid>
          ))}
        </Grid>

        {/* Form */}
        <Box sx={{ maxWidth: 700, mx: 'auto' }}>
          <Card
            sx={{
              background: '#fafafa',
              backgroundImage: 'none',
              border: 'none',
              borderRadius: 3,
              boxShadow: '0 8px 10px -1px rgba(0,0,0,0.12)',
            }}
          >
            <CardContent sx={{ p: { xs: 3, sm: 5 } }}>
              {success && (
                <Alert severity='success' sx={{ mb: 3 }}>
                  Thank you for your inquiry! A senior investment specialist will
                  contact you within 24 hours.
                </Alert>
              )}
              {error && (
                <Alert severity='error' sx={{ mb: 3 }}>
                  {error}
                </Alert>
              )}
              <Box component='form' onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      name='name'
                      label='Your Name *'
                      required
                      fullWidth
                      variant='outlined'
                      value={form.name}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      name='company'
                      label='Company/Organization'
                      fullWidth
                      variant='outlined'
                      value={form.company}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      name='email'
                      label='Email Address *'
                      type='email'
                      required
                      fullWidth
                      variant='outlined'
                      value={form.email}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      name='phone'
                      label='Phone Number (Optional)'
                      fullWidth
                      variant='outlined'
                      value={form.phone}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth>
                      <InputLabel id='consult-client-type-label'>
                        Client Type
                      </InputLabel>
                      <Select
                        labelId='consult-client-type-label'
                        label='Client Type'
                        name='clientType'
                        value={form.clientType}
                        onChange={(e) =>
                          setForm((p) => ({
                            ...p,
                            clientType: e.target.value,
                          }))
                        }
                      >
                        <MenuItem value='corporate'>Corporate Client</MenuItem>
                        <MenuItem value='institutional'>
                          Institutional Investor
                        </MenuItem>
                        <MenuItem value='hnw'>High Net Worth Individual</MenuItem>
                        <MenuItem value='trust'>Trust or Foundation</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth>
                      <InputLabel id='consult-investment-range-label'>
                        Investment Range
                      </InputLabel>
                      <Select
                        labelId='consult-investment-range-label'
                        label='Investment Range'
                        name='investmentRange'
                        value={form.investmentRange}
                        onChange={(e) =>
                          setForm((p) => ({
                            ...p,
                            investmentRange: e.target.value,
                          }))
                        }
                      >
                        <MenuItem value='1m-5m'>1m-5m</MenuItem>
                        <MenuItem value='5m-25m'>5m-25m</MenuItem>
                        <MenuItem value='25m-100m'>25m-100m</MenuItem>
                        <MenuItem value='100m-plus'>100m+</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={12}>
                    <TextField
                      name='message'
                      label='Tell us about your investment objectives and requirements'
                      fullWidth
                      multiline
                      minRows={5}
                      maxRows={10}
                      variant='outlined'
                      value={form.message}
                      onChange={handleChange}
                      slotProps={{
                        input: { sx: { minHeight: 140 } },
                      }}
                    />
                  </Grid>
                  <Grid size={12}>
                    <MuiButton
                      type='submit'
                      variant='contained'
                      fullWidth
                      size='large'
                      disabled={loading}
                      sx={{
                        backgroundColor: '#0e7490',
                        textTransform: 'none',
                        py: 1.5,
                        '&:hover': { backgroundColor: '#155e75', color: 'white' },
                        color: 'white',
                      }}
                    >
                      {loading ? 'Sending...' : 'Request Consultation'}
                    </MuiButton>
                  </Grid>
                </Grid>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Container>
    </Box>
  )
}
